import React, { useEffect, useState } from "react";
import { TOAST_SEVERITY } from "static/constants";
import { showToast } from "utils/toast";
import { deleteDevice, getAllDevices } from "app/services/devices/device";
import { Button, Tooltip } from "@mui/material";

import DeviceModal from "./modal/DeviceModal";
import { capitalizeFirstLetter } from "utils/string";
import { CheckCircle, Delete, Edit, RemoveCircle } from "@mui/icons-material";
import LoadingModal from "components/common/loading/LoadingModal";

import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal/ConfirmDeleteModal";
import { fetchManufacturers } from "app/services/dashboard/manufacturerService";
import { fetchDeviceTypes } from "app/services/dashboard/deviceType.service";
import { useDispatch } from "react-redux";

import { setManufacturers } from "app/store/manufacturers/slice/manufacturers.slice";
import { setDeviceTypes } from "app/store/deviceTypes/slice/deviceTypes.slice";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "static/pagination";
import { useSelector } from "react-redux";
import CustomTable from "components/common/table/CustomTable";
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from "material-react-table";
import { textFilter } from "utils/table";
import { broadcastData } from "utils/broadcastChannel";

const Device = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);
  const [, setIsDeviceAddReady] = useState(false);

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState();
  const [, setPageSize] = useState(10);

  const { manufacturers: manufacturerList } = useSelector(
    (state) => state.manufacturers
  );
  const { deviceTypes } = useSelector((state) => state.deviceTypes);

  const dispatch = useDispatch();

  const columnData = [
    {
      header: "Model Name",
      accessorKey: "modelName",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Display Name",
      accessorKey: "display_name",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Device Type",
      accessorKey: "deviceType",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Manufacturer",
      accessorKey: "manufacturer",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Chamber",
      accessorKey: "chamber",
      filterVariant: "text",
      filterFn: textFilter,
      Cell: ({ row }) => {
        const { chamber } = row?.original || {};
        return <span>{chamber ? capitalizeFirstLetter(chamber) : "None"}</span>;
      },
    },
    {
      header: "Generate HF Report",
      accessorKey: "hf",
      filterVariant: "checkbox",
      enableColumnFilter: false,
      Cell: ({ row }) => {
        return row?.original?.hf ? (
          <Tooltip title={"Enabled"}>
            <CheckCircle style={{ color: "#94BA33" }} />
          </Tooltip>
        ) : (
          <Tooltip title={"Disabled"}>
            <RemoveCircle style={{ color: "red" }} />
          </Tooltip>
        );
      },
    },
    {
      header: "Device Alert",
      accessorKey: "deviceAlert",
      filterVariant: "checkbox",
      enableColumnFilter: false,
      Cell: ({ row }) => {
        return row?.original?.alert_text ? (
          <Tooltip title={"Enabled"}>
            <CheckCircle style={{ color: "#94BA33" }} />
          </Tooltip>
        ) : (
          <Tooltip title={"Disabled"}>
            <RemoveCircle style={{ color: "red" }} />
          </Tooltip>
        );
      },
    },
    {
      header: "Actions",
      accessorKey: "actions",
      filterVariant: "text",
      enableColumnFilter: false,
      enableSorting: false,
      size: 60,
      accessorFn: (rowData) => {
        return (
          <div>
            <Tooltip title={"Edit"}>
              <Edit
                className="action-buttons__edit"
                onClick={() => {
                  setSelectedDevice(rowData);
                  setShowAddDeviceModal(true);
                }}
              />
            </Tooltip>
            <Tooltip title={"Delete"}>
              <Delete
                className="action-buttons__delete ml-2"
                onClick={() => {
                  setSelectedDevice(rowData);
                  setShowDeleteDeviceModal(true);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    fetchDevices();
    fetchManufacturerList();
    fetchDeviceTypesList();
  }, []);

  useEffect(() => {
    setIsDeviceAddReady(
      manufacturerList?.length > 0 && deviceTypes?.length > 0
    );
  }, [manufacturerList, deviceTypes]);

  const fetchManufacturerList = async () => {
    try {
      const manufacturers = await fetchManufacturers();
      dispatch(setManufacturers(manufacturers));
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    }
  };

  const fetchDeviceTypesList = async () => {
    try {
      const deviceTypes = await fetchDeviceTypes();
      dispatch(setDeviceTypes(deviceTypes));
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    }
  };

  const deleteSelectedDevice = async (id) => {
    try {
      setIsDeleting(true);
      setShowDeleteDeviceModal(false);
      const response = await deleteDevice(id);
      if (response) {
        broadcastData(true);
        showToast({ message: response.message });
        setIsDeleting(false);
        fetchDevices();
      }
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    } finally {
      broadcastData(false);
    }
  };

  const fetchDevices = async () => {
    try {
      setIsFetching(true);
      const devices = await getAllDevices();
      setDevices(devices);
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    } finally {
      setIsFetching(false);
    }
  };

  const getDeleteDeviceBodyText = () => {
    const { modelName } = selectedDevice;
    const confirmDeleteText = `Are you sure you want to delete: ${modelName}?`;
    return confirmDeleteText;
  };

  return (
    <div>
      <div id="userTable">
        <div className="trans-table__container">
          <CustomTable
            data={devices}
            columns={columnData}
            onChangeRowsPerPage={(e) => setPageSize(e)}
            enable
            options={{
              enableTopToolbar: true,
              enableSorting: true,
              tablePageSize: 15,
              state: {
                isLoading: isFetching,
                showColumnFilters: true,
                showGlobalFilter: true,
              },
              rowsPerPageOptions: DEFAULT_PAGE_SIZE_OPTIONS,
              renderTopToolbar: ({ table }) => (
                <div
                  className="d-flex"
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #e0e0e0",
                    gap: "10px",
                  }}
                >
                  <GlobalFilterTextField table={table} />

                  <Button
                    onClick={() => setShowAddDeviceModal(true)}
                    size="small"
                    color="secondary"
                    variant="contained"
                  >
                    + Add Device
                  </Button>
                </div>
              ),
              // rowStyle: (rowData) => {
              //   return {
              //     height: 40,
              //     backgroundColor:
              //       typeof rowData.tableData.showDetailPanel === "function"
              //         ? "rgba(142, 157, 176, .1)"
              //         : "#FFF",
              //   };
              // },
            }}
          />
        </div>
      </div>

      {showAddDeviceModal && (
        <DeviceModal
          isModalOpen={showAddDeviceModal}
          selectedDevice={selectedDevice}
          isUpdate={!!selectedDevice}
          onClose={(reason) => {
            setShowAddDeviceModal(false);
            setSelectedDevice();
            if (reason === "success") {
              fetchDevices();
            }
          }}
        />
      )}

      {showDeleteDeviceModal && (
        <ConfirmDeleteModal
          isLoading={false}
          isModalOpen={showDeleteDeviceModal}
          modalBodyText={getDeleteDeviceBodyText()}
          onClose={() => setShowDeleteDeviceModal(false)}
          onDelete={() => deleteSelectedDevice(selectedDevice._id)}
        />
      )}

      {isDeleting && <LoadingModal loadingText="Deleting Device..." />}
    </div>
  );
};

export default Device;
